import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {ProminentBannerSection_CouponSlider} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Flyout,
  GenericIconName,
  Icon,
  ModalDialog,
  PriceBoxFinalPrice,
  ProductImage,
  StickyBar,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect, useRef, useState} from 'react';
import {CouponCard} from '../../../../../features/CouponCard/CouponCard';
import {AppOnlyInfoFlyout} from '../../../../../features/CouponCard/components/AppOnlyInfoFlyout';
import {CouponInfoContentPopper} from '../../../../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {getCouponPromotionId} from '../../../../../features/Coupons/getCouponPromotionId';
import {SelectedSize} from '../../../../../features/SizeSelection/hooks/useSelectedSizeState';
import {PriceBox} from '../../../PriceAndBrandHorizontal/PriceBox';
import {
  useImagesSection as useImagesSectionMinimal,
  usePriceAndBrandSection as usePriceAndBrandSectionMinimal,
} from '../../../ProductInformationBox/MiniArticleDetail/useGetMinimalProductBulkSection';
import {
  useImagesSection,
  usePriceAndBrandSection,
} from '../../../useGetProductBulkSection';
import {useTrackCoupons} from '../utils/useTrackCoupons';
import {useCouponStickyBarOpenStateManager} from './utils/useCouponStickyBarOpenStateManager';

interface CouponStickyBarProps {
  className?: string;
  couponSlider: ProminentBannerSection_CouponSlider;
  productId: number;
  selectedSize?: SelectedSize;
  isInMiniADPDialog: boolean;
}

export const CouponStickyBar: React.FC<CouponStickyBarProps> = ({
  className,
  couponSlider,
  productId,
  selectedSize,
  isInMiniADPDialog,
}) => {
  const {t} = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const {data: productImagesSection} = useImagesSection(isInMiniADPDialog);
  const {data: productImagesSectionMinimal} = useImagesSectionMinimal(
    productId,
    !isInMiniADPDialog,
  );
  const productImages = isInMiniADPDialog
    ? productImagesSectionMinimal
    : productImagesSection;

  const {data: priceAndBrandSection} =
    usePriceAndBrandSection(isInMiniADPDialog);
  const {data: priceAndBrandSectionMinimal} = usePriceAndBrandSectionMinimal(
    productId,
    !isInMiniADPDialog,
  );
  const productInfo = isInMiniADPDialog
    ? priceAndBrandSectionMinimal
    : priceAndBrandSection;
  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const couponCardDataTestid = 'couponCard';
  const {isOpen, openedStateManagerKey} =
    useCouponStickyBarOpenStateManager(isInMiniADPDialog);
  const [infoIconContent, setInfoIconContent] = useState<CouponInfoSheet>();

  const handleInfoIconClick = (couponInfo?: CouponInfoSheet) => {
    setInfoIconContent(couponInfo);
  };

  const {featureTrackingClickCallback, trackingRef} =
    useTrackCoupons(productId);

  const onClickHandlers = () => {
    // the tracking event should be fired on open, below it's checked if the slider is currently opened and will be closed
    if (!isOpen) {
      featureTrackingClickCallback();
    }
  };

  const headerText = t('Your Coupons');

  useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        contentRef.current.removeAttribute('inert');
      } else {
        contentRef.current.setAttribute('inert', '');
      }
    }
  }, [isOpen, contentRef]);

  return (
    <>
      <StickyBar
        onClick={onClickHandlers}
        data-testid="couponStickyBar"
        className={className}
        enableInfiniteInitialBounceAnimation
        increaseBorderRadius
        disableHeaderHoverEffect
        openedStateManagerKey={openedStateManagerKey}
        closeStateMaxHeight={`${headerText.length * 16}px`}
        headerBackgroundColor={
          isOpen ? COLORS.White : couponSlider.backgroundColor.hex
        }
        OpenerContent={
          <Toggle ref={trackingRef} $color={couponSlider.foregroundColor.hex}>
            <CouponIcon asset={GenericIconName.Coupon} size="SS" />
            <span>{headerText}</span>
            <Icon
              asset={
                isOpen ? GenericIconName.ArrowLeft : GenericIconName.ArrowRight
              }
              size="XS"
            />
          </Toggle>
        }
        BodyContent={() => (
          <Content ref={contentRef}>
            {!isPriceDisplayOverhaulV2Active && (
              <Header
                data-testid="couponStickyBarContentHeader"
                data-is-open={isOpen}
              >
                <Headline
                  size="px16"
                  weight="mediumHeavy"
                  color="PrimaryCoupon"
                >
                  {t('Activate coupons for')}
                </Headline>
                <ProductSummary>
                  {productImages && (
                    <ProductImageWrapper>
                      <StyledProductImage
                        src={productImages.images[0]!.image.src}
                        widths={[120, 220, 360]}
                        ratio="1:1"
                      />
                    </ProductImageWrapper>
                  )}
                  {productInfo && (
                    <ProductInfo>
                      <Text size="px10">{productInfo.product.name}</Text>
                      <PriceBox
                        productPrice={productInfo.price}
                        selectedSize={selectedSize}
                        isInMiniADPDialog={isInMiniADPDialog}
                        productId={productId}
                      />
                    </ProductInfo>
                  )}
                </ProductSummary>
              </Header>
            )}
            <CouponCards>
              {couponSlider.coupons.map(coupon => (
                <CouponCard
                  key={getCouponPromotionId(coupon)}
                  coupon={coupon}
                  productId={productId}
                  onInfoIconClick={handleInfoIconClick}
                  isDisplayMode={false}
                />
              ))}
            </CouponCards>
          </Content>
        )}
      />
      <CouponInfoContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        data-testid={couponCardDataTestid}
        couponInfo={infoIconContent}
      />
      <AppOnlyInfoFlyout data-testid={couponCardDataTestid} />
    </>
  );
};

const Toggle = styled.div<{$color: string}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 6px;
  color: ${props => props.$color};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  text-transform: uppercase;

  > svg {
    fill: ${props => props.$color};
    position: relative;
    right: 2px;
  }
`;

const CouponIcon = styled(Icon)`
  transform: rotate(90deg);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${COLORS.BackgroundCoupon};
  height: 100%;
  max-height: 100%;

  ${PriceBoxFinalPrice} {
    padding: 0;
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 0 16px;

  transition: transform, opacity;
  transition-duration: 300ms;
  transition-timing-function: ease-in-out;

  transform: translateY(100px);
  opacity: 0;

  &[data-is-open='true'] {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Headline = styled(Text)`
  line-height: 1.5;
`;

const ProductSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: sticky;
  top: 0;
`;

const ProductImageWrapper = styled.div`
  width: 78px;
  height: 78px;
  padding: 12px;
  background-color: ${COLORS.ProductImageBackground};
  border-radius: 2px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  line-height: 1.5;
`;

const CouponCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 24px 16px;
  overflow-y: auto;

  /* keeps the top spacing previously provided by the product summary header,
     which is not rendered when ab_price_display_overhaul_v2 is active */
  &:first-child {
    padding-top: 24px;
  }
`;

const StyledProductImage = styled(ProductImage)`
  > img {
    object-fit: contain;
  }
`;
